import { sourceChannelLabels, type SourceChannel } from "@purlink/shared/attribution";
import { dashboardPresets } from "@purlink/shared/dashboard-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parseISO, subDays } from "date-fns";
import { CalendarClock, Clock, Mail, MailCheck, Sparkles, Users } from "lucide-react";
import { lazy, Suspense } from "react";
import { z } from "zod";
import { PageHeader } from "~/components/shell/app-shell";
import { stageColorCss } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Alert, Skeleton } from "~/components/ui/feedback";
import { SegmentedControl } from "~/components/ui/tabs";
import { dashboardQuery, type DashboardData } from "~/features/dashboard/api";
import { BarList, Delta, formatCount, KpiTile, WidgetCard } from "~/features/dashboard/widgets";
import { FollowUpBadge } from "~/features/leads/lead-badges";
import { errorMessage, useCurrentUser } from "~/lib/auth";
import { formatDate, hourInAppZone, todayInAppZone } from "~/lib/format";

// Recharts is the heaviest dependency here; the numbers render first.
const LeadsChart = lazy(() =>
  import("~/features/dashboard/leads-chart").then((module) => ({ default: module.LeadsChart })),
);

type Preset = (typeof dashboardPresets)[number]["key"];

const searchSchema = z.object({
  range: z
    .enum(dashboardPresets.map((p) => p.key) as [Preset, ...Preset[]])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/_app/dashboard")({
  validateSearch: searchSchema,
  component: DashboardPage,
});

function rangeFor(preset: Preset) {
  const days = dashboardPresets.find((p) => p.key === preset)!.days;
  const today = parseISO(todayInAppZone());
  return {
    from: format(subDays(today, days - 1), "yyyy-MM-dd"),
    to: format(today, "yyyy-MM-dd"),
  };
}

function greeting() {
  const hour = hourInAppZone();
  return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
}

function formatDuration(hours: number | null) {
  if (hours === null) return "—";
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${Math.round(hours * 10) / 10} h`;
  return `${Math.round((hours / 24) * 10) / 10} days`;
}

const chartColor = (index: number) => `var(--chart-${(index % 8) + 1})`;

function DashboardPage() {
  const { user } = useCurrentUser();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const preset: Preset = search.range ?? "30d";
  const dashboard = useQuery({
    ...dashboardQuery(rangeFor(preset)),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${user.name.split(" ")[0]}`}
        description={
          dashboard.data
            ? `How things look from ${formatDate(dashboard.data.range.from)} to ${formatDate(dashboard.data.range.to)}.`
            : "How your leads and outreach are doing."
        }
        actions={
          <SegmentedControl
            ariaLabel="Date range"
            value={preset}
            onValueChange={(range) =>
              void navigate({
                search: { range: range === "30d" ? undefined : range },
                replace: true,
              })
            }
            options={dashboardPresets.map((p) => ({ value: p.key, label: p.label }))}
          />
        }
      />
      {dashboard.isPending ? (
        <DashboardSkeleton />
      ) : dashboard.isError ? (
        <Alert tone="error" title="The dashboard didn't load.">
          {errorMessage(dashboard.error)}{" "}
          <Button variant="link" size="sm" onClick={() => void dashboard.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : (
        <Dashboard data={dashboard.data} stale={dashboard.isPlaceholderData} />
      )}
    </>
  );
}

function Dashboard({ data, stale }: { data: DashboardData; stale: boolean }) {
  const { kpis } = data;
  const newLeads = kpis.newLeads;
  const label = (value: string, labels: Record<string, string>) => labels[value] ?? value;

  return (
    <div
      className={stale ? "opacity-60 transition-opacity" : "transition-opacity"}
      aria-busy={stale}
    >
      <section
        aria-label="Key numbers"
        className="mb-4 grid grid-cols-2 gap-3 xl:grid-cols-3 2xl:grid-cols-6"
      >
        <KpiTile
          label="New leads"
          icon={<Sparkles />}
          value={formatCount(newLeads)}
          footer={<Delta current={newLeads} previous={kpis.previousNewLeads} />}
        />
        <KpiTile
          label="Total leads"
          icon={<Users />}
          value={formatCount(kpis.totalLeads)}
          footer={<span className="text-caption text-fg-muted">All time, across every stage</span>}
        />
        <KpiTile
          label="Emails sent"
          icon={<Mail />}
          value={formatCount(kpis.emailsSent)}
          footer={<Delta current={kpis.emailsSent} previous={kpis.previousEmailsSent} />}
        />
        <KpiTile
          label="New leads contacted"
          icon={<MailCheck />}
          value={kpis.contactedRate === null ? "—" : `${Math.round(kpis.contactedRate * 100)}%`}
          footer={
            <span className="text-caption text-fg-muted">
              {kpis.contacted} of {newLeads} emailed from Purlink
            </span>
          }
        />
        <KpiTile
          label="Time to first email"
          icon={<Clock />}
          value={formatDuration(kpis.avgHoursToFirstContact)}
          footer={<span className="text-caption text-fg-muted">Average, for new leads</span>}
        />
        <KpiTile
          label="Follow-ups due"
          icon={<CalendarClock />}
          value={formatCount(kpis.followUpsDue)}
          footer={
            kpis.followUpsDue > 0 ? (
              <Link
                to="/leads"
                search={{ followUp: "due" }}
                className="text-caption font-medium text-primary hover:underline"
              >
                See who to follow up
              </Link>
            ) : (
              <span className="text-caption text-fg-muted">You're all caught up</span>
            )
          }
        />
      </section>

      <div className="mb-4 grid gap-4 lg:grid-cols-3">
        <WidgetCard
          className="lg:col-span-2"
          title="New leads over time"
          description={data.range.bucket === "week" ? "Per week" : "Per day"}
        >
          <Suspense fallback={<Skeleton className="h-64 flex-1" />}>
            <LeadsChart data={data.leadsOverTime} bucket={data.range.bucket} />
          </Suspense>
        </WidgetCard>
        <div className="flex flex-col gap-4">
          <WidgetCard title="Follow-ups due" description="Today and overdue">
            {data.followUps.length === 0 ? (
              <p className="py-6 text-center text-small text-fg-muted">Nothing due. Nice work.</p>
            ) : (
              <ul aria-label="Follow-ups due" className="flex flex-col divide-y divide-border">
                {data.followUps.map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-3 py-2.5">
                    <Link
                      to="/leads/$leadId"
                      params={{ leadId: item.id }}
                      className="flex min-w-0 flex-col hover:text-primary"
                    >
                      <span className="truncate text-small font-medium text-fg">{item.name}</span>
                      {item.company && (
                        <span className="truncate text-caption text-fg-muted">{item.company}</span>
                      )}
                    </Link>
                    <FollowUpBadge day={item.day} />
                  </li>
                ))}
              </ul>
            )}
          </WidgetCard>
          <WidgetCard title="Leads by stage" description="Right now, all time">
            <BarList
              label="Leads by stage"
              items={data.byStage.map((stage) => ({
                key: stage.key,
                label: stage.label,
                count: stage.count,
                color: stageColorCss(stage.color),
              }))}
              empty="No leads yet."
            />
          </WidgetCard>
        </div>
      </div>

      {/* Four breakdowns: two by two keeps the grid even. */}
      <div className="grid gap-4 md:grid-cols-2">
        <WidgetCard title="Where leads came from" description="Source channel">
          <BarList
            label="Leads by source"
            total={newLeads}
            items={data.byChannel.map((row, index) => ({
              key: row.key ?? "none",
              label: label(
                row.key ?? "other",
                sourceChannelLabels as Record<SourceChannel, string>,
              ),
              count: row.count,
              color: chartColor(index),
            }))}
          />
        </WidgetCard>
        <WidgetCard title="Campaigns" description="From UTM campaign tags">
          <BarList
            label="Leads by campaign"
            empty="No campaign-tagged leads in this period."
            items={data.byCampaign.map((row, index) => ({
              key: row.key ?? "none",
              label: row.key === "__other" ? "Other campaigns" : (row.key ?? ""),
              count: row.count,
              color: chartColor(index + 1),
            }))}
          />
        </WidgetCard>
        <WidgetCard title="Services" description="What they're interested in">
          <BarList
            label="Leads by service"
            total={newLeads}
            items={data.byService.map((row, index) => ({
              key: row.key ?? "none",
              label: row.label ?? "Not specified",
              count: row.count,
              color: chartColor(index + 2),
            }))}
          />
        </WidgetCard>
        <WidgetCard title="Top companies">
          <BarList
            label="Leads by company"
            empty="No company names given in this period."
            items={data.topCompanies.map((row, index) => ({
              key: `${row.label}-${index}`,
              label: row.label,
              count: row.count,
              color: "var(--chart-7)",
            }))}
          />
        </WidgetCard>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading the dashboard" className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-3 2xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-80 rounded-xl lg:col-span-2" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    </div>
  );
}
